import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { isDashboardUrl } from '../../../component/dashboard-page/dashboard-role.model';
import {
  DASHBOARD_ORIGIN_BREADCRUMB_KEY,
  DynamicBreadcrumbService,
} from './dynamic-breadcrumb.service';

/** Strips the query string / fragment so a URL can be compared or used as a crumb link. */
function pathOf(url: string): string {
  return url.split('?')[0].split('#')[0];
}

/**
 * Prefixes a shared feature page's breadcrumb with a "Dashboard" crumb when the user
 * DRILLED into it from a dashboard (a KPI card, a chart segment, a By State / By Team
 * count, …), so the trail reads "Dashboard > Workspace - License" instead of a bare
 * "Workspace - License" — the same shape the dashboard-owned Not Activated / Overview
 * routes already have.
 *
 * WHY a published trail and not a dashboard-owned route: the drill targets (`/workspace`,
 * `/reports`) are shared pages whose own Action-column navigations, filter-restore flow and
 * sidebar highlight all assume their feature URL. Only the breadcrumb must change, so the
 * feature's wrapper route opts in with `breadcrumbTrailKey: DASHBOARD_ORIGIN_BREADCRUMB_KEY`
 * and this service publishes `[Dashboard, <feature>]` under that key; `BreadcrumbService`
 * splices it in place of the wrapper's own crumb, so every child page (Edit, Overview, …)
 * keeps the "Dashboard" ancestor too.
 *
 * Lifecycle:
 * - PUBLISHED by the feature page's DEFAULT filter-restore entry ({@link enter}) when that
 *   navigation both left a dashboard URL and carries a dashboard drill nav-state.
 * - CLEARED by any other DEFAULT entry (sidebar, search, direct URL) and by any navigation
 *   that ends OUTSIDE the feature — so it never outlives the drill-down it describes.
 * - LEFT ALONE on a RESTORE / PRESERVE entry (a return from the grid's own Action column
 *   is still the same dashboard-originated visit).
 *
 * The "Dashboard" crumb links to the EXACT dashboard URL the drill left (e.g.
 * `/dashboard/management`), so `AppComponent.goToCrumb` finds it in history and steps
 * BACK to it (rule 196) — the dashboard's return flag then restores its tab + filters.
 *
 * Must be instantiated before the first drill (it records where each navigation started),
 * so `AppComponent` injects it eagerly.
 */
@Injectable({ providedIn: 'root' })
export class DashboardOriginBreadcrumbService {
  private readonly router = inject(Router);
  private readonly dynamic = inject(DynamicBreadcrumbService);

  /** URL of the last COMPLETED navigation — the page currently (or, mid-navigation,
      just previously) on screen. */
  private lastEndedUrl = '';

  /** URL the in-flight / most recent navigation LEFT — i.e. where the page now being
      entered was navigated FROM. Captured at `NavigationStart`, so it is stable for the
      whole activation whether the page's `ngOnInit` runs before or after `NavigationEnd`. */
  private fromUrl = '';

  /** The feature URL the published trail belongs to, or `null` when nothing is published. */
  private featureUrl: string | null = null;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.fromUrl = this.lastEndedUrl;
      } else if (event instanceof NavigationEnd) {
        this.lastEndedUrl = pathOf(event.urlAfterRedirects);
        if (this.featureUrl !== null && !this.isWithin(this.lastEndedUrl, this.featureUrl)) {
          this.clear();
        }
      }
    });
  }

  /**
   * Called from a feature page's DEFAULT filter-restore entry. Publishes the
   * "Dashboard > <feature>" trail when this entry is a dashboard drill-down, otherwise
   * clears any trail left from an earlier drill.
   * @param featureUrl The feature's root URL — must equal the URL of the route declaring
   *   `breadcrumbTrailKey: DASHBOARD_ORIGIN_BREADCRUMB_KEY` (e.g. `'/workspace'`).
   * @param featureLabelKey The feature crumb's `breadcrumbs.*` i18n key.
   * @param isDashboardDrill Whether the entry's nav-state is a dashboard drill (the page's
   *   own `isDashboardCardDeepLink`). Required alongside the from-URL check so a sidebar
   *   click or a global search made WHILE on the dashboard does not claim a dashboard origin.
   */
  enter(featureUrl: string, featureLabelKey: string, isDashboardDrill: boolean): void {
    if (!isDashboardDrill || !isDashboardUrl(this.fromUrl)) {
      this.clear();
      return;
    }
    this.featureUrl = featureUrl;
    this.dynamic.set(DASHBOARD_ORIGIN_BREADCRUMB_KEY, [
      { labelKey: 'breadcrumbs.dashboard', link: this.fromUrl },
      { labelKey: featureLabelKey, link: featureUrl },
    ]);
  }

  /** Drops the published trail (no-op when none). */
  clear(): void {
    this.featureUrl = null;
    this.dynamic.clear(DASHBOARD_ORIGIN_BREADCRUMB_KEY);
  }

  /** Whether `path` is `featureUrl` itself or one of its descendants. */
  private isWithin(path: string, featureUrl: string): boolean {
    return path === featureUrl || path.startsWith(`${featureUrl}/`);
  }
}
